"use client";

// Fires a single POST to /api/locale/stamp on mount, recording the current
// URL locale as the user's preferred email language IF they haven't already
// chosen one. Cookie-/storage-guarded so navigations within the locale
// segment don't hammer the endpoint.
//
// Anonymous visitors hit the endpoint and get a quiet { ok: false } back —
// the server route handles the auth-less case without raising errors.

import { useEffect } from "react";
import type { Locale } from "@/lib/i18n/locales";

const STORAGE_KEY = "a2g_locale_stamped";

export function LocaleStamp({ locale }: { locale: Locale }) {
  useEffect(() => {
    let cancelled = false;

    try {
      const already = window.sessionStorage.getItem(STORAGE_KEY);
      if (already === locale) return;
    } catch {
      // sessionStorage unavailable (private mode, embedded). Proceed anyway —
      // the server route is idempotent so an extra POST is harmless.
    }

    fetch("/api/locale/stamp", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ locale }),
      credentials: "include",
    })
      .then(() => {
        if (cancelled) return;
        try {
          window.sessionStorage.setItem(STORAGE_KEY, locale);
        } catch {
          // ignore — see above
        }
      })
      .catch((err) => {
        // Stamping is best-effort. Failing here just means the user keeps
        // their NULL locale and continues receiving English email, which is
        // the safe fallback.
        console.warn("[locale-stamp] failed:", err);
      });

    return () => {
      cancelled = true;
    };
  }, [locale]);

  return null;
}
